<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          Person Details
        </template>
      </EsHeader>
      <div
        v-if="(!this.isRetired)
          && userHasEditAccessToPersonLocation
          && personTypeAccess
          && this.activeModule.name === 'personId'"
        class="icon-right"
      >
        <a
          role="button"
          tabindex="0"
          @keyup.enter="editPerson()"
          @click="editPerson()"
          v-if="!editCurrentPanel"
          title="Edit"
        >
          <font-awesome-icon
            :icon="['far', 'edit']"
            size="lg"
            title="Edit"
          />
        </a>
      </div>
    </div>
    <es-loader
      v-if="loading"
      style="margin: 15rem"
    />
    <es-exception
      v-else-if="loadingError"
      style="padding-bottom: 7rem;"
      exceptionMessage="There was an issue.
      Please try again or contact your administrator."
    />
    <div v-else>
      <div class="content">
        <div class="columns">
          <div class="column master-record--column">
            <es-form style="padding: 1.15rem 0rem; margin: 0rem 1.5rem 0rem 1rem">
              <template #es-form-header>
                <div
                  class="header-container"
                  v-if="anyFieldsEnabled(personInformationFieldNames)"
                >
                  <div class="header-elements">
                    <font-awesome-icon
                      :icon="['fas', 'user']"
                      size="lg"
                    />
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      Person Information
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div
                  v-if="fieldEnabled('FIRST_NAME')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('FIRST_NAME') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.firstName"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorFirstName,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('firstName')"
                            @paste="onPasteValidation('firstName')"
                            :aria-label="fieldLabel('FIRST_NAME')"
                          />
                          <span
                            v-if="hasErrorFirstName"
                            class="help is-danger"
                          >
                            {{ msgFirstName }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('MIDDLE_NAME')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('MIDDLE_NAME') }}
                        <span
                          v-if="!fieldRequired('MIDDLE_NAME')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.middleName"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorMiddleName,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('middleName')"
                            @paste="onPasteValidation('middleName')"
                            :aria-label="fieldLabel('MIDDLE_NAME')"
                          />
                          <span
                            v-if="hasErrorMiddleName"
                            class="help is-danger"
                          >
                            {{ msgMiddleName }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('LAST_NAME')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('LAST_NAME') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.lastName"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorLastName,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('lastName')"
                            @paste="onPasteValidation('lastName')"
                            :aria-label="fieldLabel('LAST_NAME')"
                          />
                          <span
                            v-if="hasErrorLastName"
                            class="help is-danger"
                          >
                            {{ msgLastName }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('SUFFIX')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('SUFFIX') }}
                        <span
                          v-if="!fieldRequired('SUFFIX')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.suffix"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorSuffix,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('suffix')"
                            @paste="onPasteValidation('suffix')"
                            :aria-label="fieldLabel('SUFFIX')"
                          />
                          <span
                            v-if="hasErrorSuffix"
                            class="help is-danger"
                          >
                            {{ msgSuffix }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('PREVIOUS_LAST_NAME')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('PREVIOUS_LAST_NAME') }}
                        <span
                          v-if="!fieldRequired('PREVIOUS_LAST_NAME')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.previousLastName"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorPreviousLastName,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('previousLastName')"
                            @paste="onPasteValidation('previousLastName')"
                            :aria-label="fieldLabel('PREVIOUS_LAST_NAME')"
                          />
                          <span
                            v-if="hasErrorPreviousLastName"
                            class="help is-danger"
                          >
                            {{ msgPreviousLastName }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('FULL_NAME')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('FULL_NAME') }}
                        <span
                          v-if="!fieldRequired('FULL_NAME')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.fullName"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorFullName,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('fullName')"
                            @paste="onPasteValidation('fullName')"
                            :aria-label="fieldLabel('FULL_NAME')"
                          />
                          <span
                            v-if="hasErrorFullName"
                            class="help is-danger"
                          >
                            {{ msgFullName }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('AKA_NAME')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('AKA_NAME') }}
                        <span
                          v-if="!fieldRequired('AKA_NAME')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.akaName"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorAkaName,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('akaName')"
                            @paste="onPasteValidation('akaName')"
                            :aria-label="fieldLabel('AKA_NAME')"
                          />
                          <span
                            v-if="hasErrorAkaName"
                            class="help is-danger"
                          >
                            {{ msgAkaName }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('GENDER_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('GENDER_CODE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <div
                            v-if="editCurrentPanel"
                            class="buttons has-addons"
                          >
                            <button
                              class="button button--form big-button"
                              v-for="(option, index) in activeGenders"
                              :key="index"
                              @click="initialInfo.genderCode = option.code"
                              :class="{ 'is-active': initialInfo.genderCode === option.code }"
                            >
                              {{ option.description }}
                            </button>
                          </div>
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapGender(initialInfo.genderCode)"
                            :aria-label="fieldLabel('GENDER_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('DATE_OF_BIRTH')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('DATE_OF_BIRTH') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            v-if="editCurrentPanel"
                            class="input calendar"
                            type="date"
                            v-model="initialInfo.dateOfBirth"
                            @change.prevent="validateField('dateOfBirth')"
                            :class="{ 'is-danger': hasErrorDateOfBirth }"
                            :aria-label="fieldLabel('DATE_OF_BIRTH')"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="displayDOBFormat(initialInfo.dateOfBirth)"
                            :aria-label="fieldLabel('DATE_OF_BIRTH')"
                          />
                          <span
                            v-if="hasErrorDateOfBirth"
                            class="help is-danger"
                          >
                            {{ msgDateOfBirth }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('SOCIAL_SECURITY_NUMBER')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('SOCIAL_SECURITY_NUMBER') }}
                        <span
                          v-if="!fieldRequired('SOCIAL_SECURITY_NUMBER')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div
                          v-if="hasModule('PRSID:PRPRF:SSN:VIEW')"
                          class="control"
                        >
                          <input
                            v-if="!hasSsnEditPermission || !editCurrentPanel"
                            class="input is-static no-pointer"
                            readonly
                            type="text"
                            :aria-label="fieldLabel('SOCIAL_SECURITY_NUMBER')"
                            :value="person.socialSecurityNumber"
                          />
                          <input
                            v-else
                            class="input"
                            placeholder="###-##-#### OR #########"
                            type="text"
                            v-model="initialInfo.socialSecurityNumber"
                            :aria-label="fieldLabel('SOCIAL_SECURITY_NUMBER')"
                            :class="{ 'is-danger': hasErrorSSN }"
                            @keypress="isSSN($event)"
                            @keyup.prevent="validateField('ssn')"
                          />
                          <span
                            v-if="hasErrorSSN"
                            class="help is-danger"
                          >
                            {{ msgSSN }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('ETHNICITY_INDICATOR')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('ETHNICITY_INDICATOR') }}
                        <span
                          v-if="!fieldRequired('ETHNICITY_INDICATOR')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="activeEthnicities"
                            keyValue="code"
                            v-on:update:values="initialInfo.ethnicityIndicator = $event;
                              validateSelectField('ethnicityIndicator')"
                            :clearOut="true"
                            ref="resetEthnicity"
                            :preSelected="preSelectedEthnicity"
                            :hasError="hasErrorEthnicityIndicator"
                            :errorMsg="msgEthnicityIndicator"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapEthnicity(initialInfo.ethnicityIndicator)"
                            :aria-label="fieldLabel('ETHNICITY_INDICATOR')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RACE_ETHNIC_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RACE_ETHNIC_CODE') }}
                        <span
                          v-if="!fieldRequired('RACE_ETHNIC_CODE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="userAvailableRaces"
                            keyValue="code"
                            v-on:update:values="
                              updateRaces('raceEthnicCode', $event);
                              validateSelectField('raceEthnicCode')"
                            :clearOut="true"
                            ref="resetRaceEthnicCode"
                            :preSelected="preSelectedRaceEthnicCode"
                            :hasError="hasErrorRaceEthnicCode"
                            :errorMsg="msgRaceEthnicCode"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="initialInfo.raceEthnicCode.length
                              ? mapRace(initialInfo.raceEthnicCode[0].code) : '--'"
                            :aria-label="fieldLabel('RACE_ETHNIC_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RACE2_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RACE2_CODE') }}
                        <span
                          v-if="!fieldRequired('RACE2_CODE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="userAvailableRaces"
                            keyValue="code"
                            v-on:update:values="
                              updateRaces('race2Code', $event);
                              validateSelectField('race2Code')"
                            :clearOut="true"
                            ref="resetRace2Code"
                            :preSelected="preSelectedRace2Code"
                            :disabled="!initialInfo.raceEthnicCode.length"
                            :hasError="hasErrorRace2Code"
                            :errorMsg="msgRace2Code"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="initialInfo.race2Code.length
                              ? mapRace(initialInfo.race2Code[0].code) : '--'"
                            :aria-label="fieldLabel('RACE2_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RACE3_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RACE3_CODE') }}
                        <span
                          v-if="!fieldRequired('RACE3_CODE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="userAvailableRaces"
                            keyValue="code"
                            v-on:update:values="
                              updateRaces('race3Code', $event);
                              validateSelectField('race3Code')"
                            :clearOut="true"
                            ref="resetRace3Code"
                            :preSelected="preSelectedRace3Code"
                            :disabled="!initialInfo.race2Code.length"
                            :hasError="hasErrorRace3Code"
                            :errorMsg="msgRace3Code"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="initialInfo.race3Code.length
                              ? mapRace(initialInfo.race3Code[0].code) : '--'"
                            :aria-label="fieldLabel('RACE3_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RACE4_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RACE4_CODE') }}
                        <span
                          v-if="!fieldRequired('RACE4_CODE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="userAvailableRaces"
                            keyValue="code"
                            v-on:update:values="
                              updateRaces('race4Code', $event);
                              validateSelectField('race4Code')"
                            :clearOut="true"
                            ref="resetRace4Code"
                            :preSelected="preSelectedRace4Code"
                            :disabled="!initialInfo.race3Code.length"
                            :hasError="hasErrorRace4Code"
                            :errorMsg="msgRace4Code"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="initialInfo.race4Code.length
                              ? mapRace(initialInfo.race4Code[0].code) : '--'"
                            :aria-label="fieldLabel('RACE4_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RACE5_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RACE5_CODE') }}
                        <span
                          v-if="!fieldRequired('RACE5_CODE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="userAvailableRaces"
                            keyValue="code"
                            v-on:update:values="initialInfo.race5Code = $event;
                              validateSelectField('race5Code')"
                            :clearOut="true"
                            ref="resetRace5Code"
                            :preSelected="preSelectedRace5Code"
                            :disabled="!initialInfo.race4Code.length"
                            :hasError="hasErrorRace5Code"
                            :errorMsg="msgRace5Code"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="initialInfo.race5Code.length
                              ? mapRace(initialInfo.race5Code[0].code) : '--'"
                            :aria-label="fieldLabel('RACE5_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('ETHNIC_SUB_GROUP')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('ETHNIC_SUB_GROUP') }}
                        <span
                          v-if="!fieldRequired('ETHNIC_SUB_GROUP')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="activeEthnicSubGroups"
                            keyValue="code"
                            v-on:update:values="initialInfo.ethnicSubGroup = $event;
                              validateSelectField('ethnicSubGroup')"
                            :clearOut="true"
                            ref="resetEthnicSubGroups"
                            :preSelected="preSelectedEthnicSubGroup"
                            :hasError="hasErrorEthnicSubGroup"
                            :errorMsg="msgEthnicSubGroup"
                          />
                          <input
                            v-else class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel
                            }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapEthnicSubGroup(initialInfo.ethnicSubGroup)"
                            :aria-label="fieldLabel('ETHNIC_SUB_GROUP')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('STATE_STUDENT_ID')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('STATE_STUDENT_ID') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal is-flex">
                          <input
                            class="input"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel
                            }"
                            :disabled="editCurrentPanel"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="formatStateID(initialInfo.stateStudentId
                              ? initialInfo.stateStudentId
                              : initialInfo.personId)"
                            :aria-label="fieldLabel('STATE_STUDENT_ID')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
          </div>
          <div class="column master-record--column border-left">
            <es-form style="padding: 1.15rem 0.5rem; margin: 0rem 1.5rem 0rem 1rem">
              <template #es-form-header>
                <div
                  v-if="anyFieldsEnabled(contactInformationFieldNames)"
                  class="header-container"
                >
                  <div class="header-elements">
                    <font-awesome-icon
                      :icon="['fas', 'address-book']"
                      size="lg"
                    />
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      Contact Information
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div
                  v-if="fieldEnabled('STREET_1')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('STREET_1') }}
                        <span
                          v-if="!fieldRequired('STREET_1')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.street1"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorStreet1,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('street1')"
                            @paste="onPasteValidation('street1')"
                            :aria-label="fieldLabel('STREET_1')"
                          />
                          <span
                            v-if="hasErrorStreet1"
                            class="help is-danger"
                          >
                            {{ msgStreet1 }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('STREET_2')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('STREET_2') }}
                        <span
                          v-if="!fieldRequired('STREET_2')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.street2"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorStreet2,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('street2')"
                            @paste="onPasteValidation('street2')"
                            :aria-label="fieldLabel('STREET_2')"
                          />
                          <span
                            v-if="hasErrorStreet2"
                            class="help is-danger"
                          >
                            {{ msgStreet2 }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('CITY')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('CITY') }}
                        <span
                          v-if="!fieldRequired('CITY')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.city"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorCity,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('city')"
                            @paste="onPasteValidation('city')"
                            :aria-label="fieldLabel('CITY')"
                          />
                          <span
                            v-if="hasErrorCity"
                            class="help is-danger"
                          >
                            {{ msgCity }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('STATE_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('STATE_CODE') }}
                        <span
                          v-if="!fieldRequired('STATE_CODE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="activeStates"
                            keyValue="code"
                            v-on:update:values="initialInfo.stateCode = $event;
                              validateSelectField('stateCode')"
                            :clearOut="true"
                            ref="resetState"
                            :preSelected="preSelectedState"
                            :hasError="hasErrorState"
                            :errorMsg="msgState"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{ 'is-static no-pointer': !editCurrentPanel }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapState(initialInfo.stateCode)"
                            :aria-label="fieldLabel('STATE_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('ZIP')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('ZIP') }}
                        <span
                          v-if="!fieldRequired('ZIP')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            maxlength="17"
                            v-model="initialInfo.zip"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorZip,
                            }"
                            :readonly="!editCurrentPanel"
                            @keypress="isWholeNumbers($event)"
                            @keyup.prevent="validateInputField('zip')"
                            @paste="onPasteValidation('zip', $event)"
                            :aria-label="fieldLabel('ZIP')"
                          />
                          <span
                            v-if="hasErrorZip"
                            class="help is-danger"
                          >
                            {{ msgZip }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('GEOCODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('GEOCODE') }}
                        <span
                          v-if="!fieldRequired('GEOCODE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            maxlength="22"
                            v-model="initialInfo.geocode"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorGeocode,
                            }"
                            :readonly="!editCurrentPanel"
                            @keypress="isWholeNumbers($event)"
                            @keyup.prevent="validateInputField('geocode')"
                            @paste="onPasteValidation('geocode', $event)"
                            :aria-label="fieldLabel('GEOCODE')"
                          />
                          <span
                            v-if="hasErrorGeocode"
                            class="help is-danger"
                          >
                            {{ msgGeocode }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
            <es-form style="padding: 1.15rem 0.5rem; margin: 0rem 1.5rem 0rem 1rem">
              <template #es-form-header>
                <div
                  v-if="anyFieldsEnabled(credentialInformationFieldNames)"
                  class="header-container"
                >
                  <div class="header-elements">
                    <font-awesome-icon
                      :icon="['fas', 'diploma']"
                      size="lg"
                    />
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      Credential Information
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div
                  v-if="fieldEnabled('HIGHEST_DEGREE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('HIGHEST_DEGREE') }}
                        <span
                          v-if="!fieldRequired('HIGHEST_DEGREE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="activeHighestDegrees"
                            keyValue="code"
                            v-on:update:values="initialInfo.highestDegree = $event;
                              validateSelectField('highestDegree')"
                            :clearOut="true"
                            :preSelected="preSelectedHighestDegree"
                            :hasError="hasErrorHighestDegree"
                            :errorMsg="msgHighestDegree"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{ 'is-static no-pointer': !editCurrentPanel }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapHighestDegree(initialInfo.highestDegree)"
                            :aria-label="fieldLabel('HIGHEST_DEGREE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('SUBJECT_MATTER_AREA')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('SUBJECT_MATTER_AREA') }}
                        <span
                          v-if="!fieldRequired('SUBJECT_MATTER_AREA')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="activeSubjectMatterAreas"
                            keyValue="code"
                            v-on:update:values="initialInfo.subjectMatterArea = $event;
                              validateSelectField('subjectMatterArea')"
                            :clearOut="true"
                            :preSelected="preSelectedSubjectMatterArea"
                            :hasError="hasErrorSubjectMatterArea"
                            :errorMsg="msgSubjectMatterArea"
                          />
                          <input
                            v-else class="input"
                            :class="{ 'is-static no-pointer': !editCurrentPanel }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapSubjectMatterArea(initialInfo.subjectMatterArea)"
                            :aria-label="fieldLabel('SUBJECT_MATTER_AREA')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('DEGREE_PROVIDER')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('DEGREE_PROVIDER') }}
                        <span
                          v-if="!fieldRequired('DEGREE_PROVIDER')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.degreeProvider"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorDegreeProvider,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('degreeProvider')"
                            @paste="onPasteValidation('degreeProvider')"
                            :aria-label="fieldLabel('DEGREE_PROVIDER')"
                          />
                          <span
                            v-if="hasErrorDegreeProvider"
                            class="help is-danger"
                          >
                            {{ msgDegreeProvider }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('CERTIFICATE_NUMBER')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('CERTIFICATE_NUMBER') }}
                        <span
                          v-if="!fieldRequired('CERTIFICATE_NUMBER')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.certificateNumber"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorCertificateNumber,
                            }"
                            :readonly="!editCurrentPanel"
                            @keypress="isWholeNumbers($event)"
                            @keyup.prevent="validateInputField('certificateNumber')"
                            @paste="onPasteValidation('certificateNumber')"
                            :aria-label="fieldLabel('CERTIFICATE_NUMBER')"
                          />
                          <span
                            v-if="hasErrorCertificateNumber"
                            class="help is-danger"
                          >
                            {{ msgCertificateNumber }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('CERTIFICATE_TYPE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('CERTIFICATE_TYPE') }}
                        <span
                          v-if="!fieldRequired('CERTIFICATE_TYPE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="activeCertificateTypes"
                            keyValue="code"
                            v-on:update:values="initialInfo.certificateType = $event;
                              validateSelectField('certificateType')"
                            :clearOut="true"
                            :preSelected="preSelectedCertificateType"
                            :hasError="hasErrorCertificateType"
                            :errorMsg="msgCertificateType"
                          />
                          <input
                            v-else class="input"
                            :class="{ 'is-static no-pointer': !editCurrentPanel }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapCertificateType(initialInfo.certificateType)"
                            :aria-label="fieldLabel('CERTIFICATE_TYPE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
          </div>
          <div class="column master-record--column border-left">
            <es-form style="padding: 1.15rem 0.5rem; margin: 0rem 1.5rem 0rem 1rem">
              <template #es-form-header>
                <div
                  v-if="anyFieldsEnabled(otherInformationFieldNames)"
                  class="header-container"
                >
                  <div class="header-elements">
                    <font-awesome-icon
                      :icon="['fas', 'list-alt']"
                      size="lg"
                    />
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      Other Information
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div
                  v-if="fieldEnabled('BIRTH_LOCATION')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('BIRTH_LOCATION') }}
                        <span
                          v-if="!fieldRequired('BIRTH_LOCATION')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.birthLocation"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorBirthLocation,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('birthLocation')"
                            @paste="onPasteValidation('birthLocation')"
                            :aria-label="fieldLabel('BIRTH_LOCATION')"
                          />
                          <span
                            v-if="hasErrorBirthLocation"
                            class="help is-danger"
                          >
                            {{ msgBirthLocation }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RESIDENCY_STATUS')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RESIDENCY_STATUS') }}
                        <span
                          v-if="!fieldRequired('RESIDENCY_STATUS')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <es-select
                            v-if="editCurrentPanel"
                            valueTpl="${description}"
                            :setValues="activeResidencyStatuses"
                            keyValue="code"
                            v-on:update:values="initialInfo.residencyStatus = $event;
                              validateSelectField('residencyStatus')"
                            :clearOut="true"
                            ref="resetResidencyStatus"
                            :preSelected="preSelectedResidencyStatus"
                            :hasError="hasErrorResidencyStatus"
                            :errorMsg="msgResidencyStatus"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{ 'is-static no-pointer': !editCurrentPanel }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="mapResidencyStatus(initialInfo.residencyStatus)"
                            :aria-label="fieldLabel('RESIDENCY_STATUS')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RESIDENCY_EXPIRATION_DATE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RESIDENCY_EXPIRATION_DATE') }}
                        <span
                          v-if="!fieldRequired('RESIDENCY_EXPIRATION_DATE')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            v-if="editCurrentPanel"
                            class="input calendar"
                            type="date"
                            v-model="initialInfo.residencyExpirationDate"
                            :class="{ 'is-danger': hasErrorResidencyExpirationDate }"
                            @change.prevent="validateDateField('residencyExpirationDate')"
                            :aria-label="fieldLabel('RESIDENCY_EXPIRATION_DATE')"
                          />
                          <input
                            v-else
                            class="input"
                            :class="{ 'is-static no-pointer': !editCurrentPanel }"
                            :readonly="!editCurrentPanel"
                            type="text"
                            :value="initialInfo.residencyExpirationDate | formatDateMMDDYYYY"
                            :aria-label="fieldLabel('RESIDENCY_EXPIRATION_DATE')"
                          />
                        </div>
                        <span
                          v-if="hasErrorResidencyExpirationDate"
                          class="help is-danger"
                        >
                          {{ msgResidencyExpirationDate }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('MOTHERS_MAIDEN_NAME')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('MOTHERS_MAIDEN_NAME') }}
                        <span
                          v-if="!fieldRequired('MOTHERS_MAIDEN_NAME')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            class="input"
                            type="text"
                            v-model="initialInfo.mothersMaidenName"
                            :class="{
                              'is-static no-pointer': !editCurrentPanel,
                              'is-danger': hasErrorMothersMaidenName,
                            }"
                            :readonly="!editCurrentPanel"
                            @keyup.prevent="validateInputField('mothersMaidenName')"
                            @paste="onPasteValidation('mothersMaidenName')"
                            :aria-label="fieldLabel('MOTHERS_MAIDEN_NAME')"
                          />
                          <span
                            v-if="hasErrorMothersMaidenName"
                            class="help is-danger"
                          >
                            {{ msgMothersMaidenName }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
            <es-form style="padding: 1.15rem 0.5rem; margin: 0rem 1.5rem 0rem 1rem">
              <template #es-form-header>
                <div
                  v-if="anyFieldsEnabled(cdfFieldNames)"
                  class="header-container"
                >
                  <div class="header-elements">
                    <font-awesome-icon
                      :icon="['fas', 'database']"
                      size="lg"
                    />
                  </div>
                  <div class="header-elements">
                    <h6 class="title">
                      Customer Defined Fields
                    </h6>
                  </div>
                </div>
              </template>
              <template #es-form>
                <div
                  v-if="fieldEnabled('cdf_1')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_1') }}
                        <span
                          v-if="!fieldRequired('cdf_1')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_1"
                              readonly
                              :aria-label="fieldLabel('cdf_1')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_1"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf1,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_1')"
                              @paste="onPasteValidation('cdf_1')"
                              :aria-label="fieldLabel('cdf_1')"
                            />
                            <span
                              v-if="hasErrorCdf1"
                              class="help is-danger"
                            >
                              {{ msgCdf1 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_2')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_2') }}
                        <span
                          v-if="!fieldRequired('cdf_2')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_2"
                              readonly
                              :aria-label="fieldLabel('cdf_2')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_2"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf2,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_2')"
                              @paste="onPasteValidation('cdf_2')"
                              :aria-label="fieldLabel('cdf_2')"
                            />
                            <span
                              v-if="hasErrorCdf2"
                              class="help is-danger"
                            >
                              {{ msgCdf2 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_3')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_3') }}
                        <span
                          v-if="!fieldRequired('cdf_3')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_3"
                              readonly
                              :aria-label="fieldLabel('cdf_3')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text" v-model="initialInfo.studentCdf.cdf_3"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf3,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_3')"
                              @paste="onPasteValidation('cdf_3')"
                              :aria-label="fieldLabel('cdf_3')"
                            />
                            <span
                              v-if="hasErrorCdf3"
                              class="help is-danger"
                            >
                              {{ msgCdf3 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_4')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_4') }}
                        <span
                          v-if="!fieldRequired('cdf_4')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_4"
                              readonly
                              :aria-label="fieldLabel('cdf_4')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_4"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf4,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_4')"
                              @paste="onPasteValidation('cdf_4')"
                              :aria-label="fieldLabel('cdf_4')"
                            />
                            <span
                              v-if="hasErrorCdf4"
                              class="help is-danger"
                            >
                              {{ msgCdf4 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_5')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_5') }}
                        <span
                          v-if="!fieldRequired('cdf_5')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_5"
                              readonly
                              :aria-label="fieldLabel('cdf_5')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text" v-model="initialInfo.studentCdf.cdf_5"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf5,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_5')"
                              @paste="onPasteValidation('cdf_5')"
                              :aria-label="fieldLabel('cdf_5')"
                            />
                            <span
                              v-if="hasErrorCdf5"
                              class="help is-danger"
                            >
                              {{ msgCdf5 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_6')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_6') }}
                        <span
                          v-if="!fieldRequired('cdf_6')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_6"
                              readonly
                              :aria-label="fieldLabel('cdf_6')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_6"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf6,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_6')"
                              @paste="onPasteValidation('cdf_6')"
                              :aria-label="fieldLabel('cdf_6')"
                            />
                            <span
                              v-if="hasErrorCdf6"
                              class="help is-danger"
                            >
                              {{ msgCdf6 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_7')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_7') }}
                        <span
                          v-if="!fieldRequired('cdf_7')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_7"
                              readonly
                              :aria-label="fieldLabel('cdf_7')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_7"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf7,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_7')"
                              @paste="onPasteValidation('cdf_7')"
                              :aria-label="fieldLabel('cdf_7')"
                            />
                            <span
                              v-if="hasErrorCdf7"
                              class="help is-danger"
                            >
                              {{ msgCdf7 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_8')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_8') }}
                        <span
                          v-if="!fieldRequired('cdf_8')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_8"
                              readonly
                              :aria-label="fieldLabel('cdf_8')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_8"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf8,
                              }"
                              :readonly="!editCurrentPanel"
                              @keyup.prevent="validateInputField('cdf_8')"
                              @paste="onPasteValidation('cdf_8')"
                              :aria-label="fieldLabel('cdf_8')"
                            />
                            <span
                              v-if="hasErrorCdf8"
                              class="help is-danger"
                            >
                              {{ msgCdf8 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_9')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_9') }}
                        <span
                          v-if="!fieldRequired('cdf_9')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_9"
                              readonly
                              :aria-label="fieldLabel('cdf_9')"
                            />
                          </span>
                          <span v-else>
                            <input
                              v-if="editCurrentPanel"
                              class="input calendar"
                              type="date"
                              v-model="initialInfo.studentCdf.cdf_9"
                              @change.prevent="validateDateField('cdf_9')"
                              :class="{ 'is-danger': hasErrorCdf9 }"
                              :aria-label="fieldLabel('cdf_9')"
                            />
                            <input
                              v-else
                              class="input"
                              :class="{ 'is-static no-pointer': !editCurrentPanel }"
                              :readonly="!editCurrentPanel"
                              type="text"
                              :value="initialInfo.studentCdf.cdf_9 | formatDateMMDDYYYY"
                              :aria-label="fieldLabel('cdf_9')"
                            />
                            <span
                              v-if="hasErrorCdf9"
                              class="help is-danger"
                            >
                              {{ msgCdf9 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_10')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_10') }}
                        <span
                          v-if="!fieldRequired('cdf_10')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_10"
                              readonly
                              :aria-label="fieldLabel('cdf_10')"
                            />
                          </span>
                          <span v-else>
                            <input
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_10"
                              :class="{
                                'is-static no-pointer': !editCurrentPanel,
                                'is-danger': hasErrorCdf10,
                              }"
                              :readonly="!editCurrentPanel"
                              onPaste="return false"
                              @keypress="isWholeNumbers($event)"
                              @keyup.prevent="validateInputField('cdf_10')"
                              @paste="onPasteValidation('cdf_10', $event)"
                              :aria-label="fieldLabel('cdf_10')"
                            />
                            <span
                              v-if="hasErrorCdf10"
                              class="help is-danger"
                            >
                              {{ msgCdf10 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('cdf_11')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('cdf_11') }}
                        <span
                          v-if="!fieldRequired('cdf_11')
                            && editCurrentPanel"
                          class="label-optional has-text-grey-light"
                        >
                          &nbsp;(optional)
                        </span>
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <span v-if="isRetired">
                            <input
                              class="input is-static"
                              type="text"
                              :value="initialInfo.personCdf.cdf_11"
                              readonly
                              :aria-label="fieldLabel('cdf_11')"
                            />
                          </span>
                          <span v-else>
                            <input
                              v-if="!editCurrentPanel"
                              class="input is-static no-pointer"
                              type="text"
                              :value="getCdfDisplayValue('cdf_11')"
                              readonly
                              :aria-label="fieldLabel('cdf_11')"
                            />
                            <input
                              v-else-if="!isLookup('cdf_11')"
                              class="input"
                              type="text"
                              v-model="initialInfo.studentCdf.cdf_11"
                              :class="{
                                'is-danger': hasErrorCdf11,
                              }"
                              @keyup.prevent="validateInputField('cdf_11')"
                              @paste="onPasteValidation('cdf_11')"
                              :aria-label="fieldLabel('cdf_11')"
                            />
                            <es-select v-else
                              valueTpl="${fieldValue}"
                              :setValues="getCdfValidValues('cdf_11')"
                              keyValue="fieldValue"
                                v-on:update:values="updateLookupCdfValue('cdf_11', $event)"
                              v-bind:clearOut="true"
                              ref="resetCdf11"
                                :preSelected="preSelectedCdf11"
                              :hasError="hasErrorCdf11"
                              :errorMsg="msgCdf11"
                            />
                            <span
                              v-if="hasErrorCdf11"
                              class="help is-danger"
                            >
                              {{ msgCdf11 }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </es-form>
          </div>
        </div>
      </div>
      <div
        v-if="editCurrentPanel"
        class="is-flex flex-justify--end flex-align--center action-buttons"
        style="margin-right: 5.15rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click.prevent="cancelUpdate()"
          style="margin-right: 1rem"
        >
          Cancel
        </button>
        <button
          class="button is-primary"
          @click.prevent="updatePerson()"
        >
          Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import StateIdUtils from '@/mixins/StateIdUtils';

library.add(
  faAngleDoubleLeft,
  faEdit,
);

export default {
  mixins: [
    DateUtils,
    FieldLabels,
    NotificationManager,
    PermissionManager,
    StateIdUtils,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
  },
  props: {
    isRetired: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object,
      required: true,
    },
    userHasEditAccessToPersonLocation: {
      type: Boolean,
    },
  },
  watch: {
    person: {
      handler(newPerson) {
        if (newPerson) {
          this.initialInfo = JSON.parse(JSON.stringify(newPerson));
          this.modifyRaces();
          this.initialInfo = JSON.parse(JSON.stringify(this.initialInfo));
          if (this.ssnMaskingEnabled) {
            this.initialInfo.socialSecurityNumber = null;
          }
        }
      },
      immediate: true,
    },
  },
  created() {
    this.loadCDFValidValues().catch((error) => {
      console.error('Failed to load CDF values', error);
    });
  },
  data() {
    return this.initialData();
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
    }),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeCdfValidValues',
      'activeGenders',
      'activeStates',
      'activeRaces',
      'activeEthnicities',
      'activeEthnicSubGroups',
      'activeResidencyStatuses',
      'activeStates',
      'activeHighestDegrees',
      'activeSubjectMatterAreas',
      'activeCertificateTypes',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldLengthsByPersonType',
      'getFieldEnabledForPersonType',
      'getFieldRequiredForPersonType',
    ]),
    cdfFieldNames() {
      return ['cdf_1',
        'cdf_2',
        'cdf_3',
        'cdf_4',
        'cdf_5',
        'cdf_6',
        'cdf_7',
        'cdf_8',
        'cdf_9',
        'cdf_10',
        'cdf_11',
      ];
    },
    contactInformationFieldNames() {
      return ['STREET_1',
        'STREET_2',
        'CITY',
        'STATE_CODE',
        'ZIP',
        'GEOCODE',
      ];
    },
    credentialInformationFieldNames() {
      return ['HIGHEST_DEGREE',
        'SUBJECT_MATTER_AREA',
        'DEGREE_PROVIDER',
        'CERTIFICATE_NUMBER',
        'CERTIFICATE_TYPE',
      ];
    },
    allowSinSsn() {
      return this.properties && (this.properties['GENERAL-SSN_ALLOW_S'] === 'true' || this.properties['GENERAL-SSN_ALLOW_S'] === true);
    },
    currentPersonTypeCategory() {
      return this.userPersonTypes.find(r => r.code === this.person.personType).category;
    },
    hasSsnEditPermission() {
      return this.hasModule('PRSID:PRPRF:SSN:EDIT');
    },
    noDataChange() {
      if (JSON.stringify(this.initialInfo) === JSON.stringify(this.cachedInfo)) {
        return true;
      } return false;
    },
    otherInformationFieldNames() {
      return ['BIRTH_LOCATION',
        'RESIDENCY_STATUS',
        'RESIDENCY_EXPIRATION_DATE',
        'MOTHERS_MAIDEN_NAME',
      ];
    },
    personInformationFieldNames() {
      return ['FIRST_NAME',
        'MIDDLE_NAME',
        'LAST_NAME',
        'SUFFIX',
        'PREVIOUS_LAST_NAME',
        'AKA_NAME',
        'GENDER_CODE',
        'DATE_OF_BIRTH',
        'SOCIAL_SECURITY_NUMBER',
        'ETHNICITY_INDICATOR',
        'RACE_ETHNIC_CODE',
        'RACE2_CODE',
        'RACE3_CODE',
        'RACE4_CODE',
        'RACE5_CODE',
        'ETHNIC_SUB_GROUP',
        'STATE_STUDENT_ID',
      ];
    },
    personTypeAccess() {
      if (this.$store.state.user.userPersonTypes
        .some(item => item.personType.code === this.person.personType)) {
        return true;
      }
      return false;
    },
    preSelectedEthnicity() {
      return [this.activeEthnicities.find(e => e.code === this.initialInfo.ethnicityIndicator)];
    },
    preSelectedEthnicSubGroup() {
      return [this.activeEthnicSubGroups.find(e => e.code === this.initialInfo.ethnicSubGroup)];
    },
    preSelectedRaceEthnicCode() {
      if (this.initialInfo.raceEthnicCode.length) {
        return [this.activeRaces.find(e => e.code === this.initialInfo.raceEthnicCode[0].code)];
      }
      return [];
    },
    preSelectedRace2Code() {
      if (this.initialInfo.race2Code.length) {
        return [this.activeRaces.find(e => e.code === this.initialInfo.race2Code[0].code)];
      }
      return [];
    },
    preSelectedRace3Code() {
      if (this.initialInfo.race3Code.length) {
        return [this.activeRaces.find(e => e.code === this.initialInfo.race3Code[0].code)];
      }
      return [];
    },
    preSelectedRace4Code() {
      if (this.initialInfo.race4Code.length) {
        return [this.activeRaces.find(e => e.code === this.initialInfo.race4Code[0].code)];
      }
      return [];
    },
    preSelectedRace5Code() {
      if (this.initialInfo.race5Code.length) {
        return [this.activeRaces.find(e => e.code === this.initialInfo.race5Code[0].code)];
      }
      return [];
    },
    preSelectedResidencyStatus() {
      return [this.activeResidencyStatuses.find(e => e.code === this.initialInfo.residencyStatus)];
    },
    preSelectedState() {
      return [this.activeStates.find(e => e.code === this.initialInfo.stateCode)];
    },
    preSelectedHighestDegree() {
      return [this.activeHighestDegrees.find(e => e.code === this.initialInfo.highestDegree)];
    },
    preSelectedSubjectMatterArea() {
      return [this.activeSubjectMatterAreas
        .find(e => e.code === this.initialInfo.subjectMatterArea)];
    },
    preSelectedCertificateType() {
      return [this.activeCertificateTypes.find(e => e.code === this.initialInfo.certificateType)];
    },
    preSelectedCdf11() {
      const selectedValue = this.getCdfDisplayValue('cdf_11');
      if (!selectedValue || !this.isLookup('cdf_11')) {
        return [];
      }
      const match = this.getCdfValidValues('cdf_11')
        .find(value => value.fieldValue === selectedValue);
      return match ? [match] : [];
    },
    ssnMaskingEnabled() {
      return this.isMaskingEnabled(this.initialInfo.socialSecurityNumber);
    },
    userSelectedRaces() {
      const userSelection = [
        this.initialInfo.raceEthnicCode,
        this.initialInfo.race2Code,
        this.initialInfo.race3Code,
        this.initialInfo.race4Code,
        this.initialInfo.race5Code,
      ];
      const selectedRaceCodes = [];
      userSelection.forEach((selection) => {
        const raceCodes = selection.map(r => r.code);
        selectedRaceCodes.push(...raceCodes);
      });
      return selectedRaceCodes;
    },
    userAvailableRaces() {
      const allAvailableRaces = this.activeRaces.map(item => ({
        code: item.code,
        description: item.description,
      }));
      return allAvailableRaces.filter(race => !this.userSelectedRaces.includes(race.code));
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadCDFValidValues',
    ]),
    anyFieldsEnabled(fieldNames) {
      // check whether any field is enabled
      const items = fieldNames.filter(fn => this.fieldEnabled(fn));
      return (items && items.length > 0);
    },
    cacheValues() {
      this.cachedInfo = JSON.parse(JSON.stringify(this.initialInfo));
    },
    cancelUpdate() {
      this.resetData();
      this.resetErrors();
      this.editCurrentPanel = false;
      this.hideNotification();
    },
    checkFieldValidations() {
      const inputFields = [
        'akaName', 'birthLocation', 'cdf_1', 'cdf_2', 'cdf_3', 'cdf_4', 'cdf_5',
        'cdf_6', 'cdf_7', 'cdf_8', 'cdf_10', 'cdf_11', 'city', 'degreeProvider',
        'firstName', 'fullName', 'geocode', 'lastName', 'middleName', 'mothersMaidenName',
        'previousLastName', 'suffix', 'street1', 'street2', 'zip',
      ];
      inputFields.forEach(field => this.validateInputField(field));

      const selectFields = [
        'certificateType', 'ethnicSubGroup', 'ethnicityIndicator', 'highestDegree',
        'race2Code', 'race3Code', 'race4Code', 'race5Code', 'raceEthnicCode',
        'residencyStatus', 'stateCode', 'subjectMatterArea',
      ];
      selectFields.forEach(field => this.validateSelectField(field));

      const dateFields = [
        'residencyExpirationDate', 'cdf_9',
      ];
      dateFields.forEach(field => this.validateDateField(field));

      this.validateField('dateOfBirth');

      const ssn = this.initialInfo.socialSecurityNumber;
      const ssnRequired = this.fieldRequired('SOCIAL_SECURITY_NUMBER');
      // validate SSN only if user has edit permission
      if (this.hasModule('PRSID:PRPRF:SSN:EDIT') && ((!this.ssnMaskingEnabled && ssn) || (ssnRequired && !ssn))) {
        this.validateField('ssn');
      }

      const errorFields = [
        'hasErrorAkaName',
        'hasErrorBirthLocation',
        'hasErrorCdf1',
        'hasErrorCdf2',
        'hasErrorCdf3',
        'hasErrorCdf4',
        'hasErrorCdf5',
        'hasErrorCdf6',
        'hasErrorCdf7',
        'hasErrorCdf8',
        'hasErrorCdf9',
        'hasErrorCdf10',
        'hasErrorCdf11',
        'hasErrorCertificateNumber',
        'hasErrorCertificateType',
        'hasErrorCity',
        'hasErrorDateOfBirth',
        'hasErrorDegreeProvider',
        'hasErrorEthnicSubGroup',
        'hasErrorEthnicityIndicator',
        'hasErrorFirstName',
        'hasErrorFullName',
        'hasErrorGeoCode',
        'hasErrorHighestDegree',
        'hasErrorLastName',
        'hasErrorMiddleName',
        'hasErrorMothersMaidenName',
        'hasErrorPreviousLastName',
        'hasErrorRace2Code',
        'hasErrorRace3Code',
        'hasErrorRace4Code',
        'hasErrorRace5Code',
        'hasErrorRaceEthnicCode',
        'hasErrorResidencyExpirationDate',
        'hasErrorResidencyStatus',
        'hasErrorSSN',
        'hasErrorState',
        'hasErrorStreet1',
        'hasErrorStreet2',
        'hasErrorSubjectMatterArea',
        'hasErrorSuffix',
        'hasErrorZip',
      ];

      const err = errorFields.some(field => this[field]);

      if (err) {
        this.showNotification('danger', [this.formErrorMsg]);
      } else {
        this.resetNotifications();
      }

      return err;
    },
    cleanUpFields() {
      this.extractFirstCode('ethnicityIndicator');
      this.extractFirstCode('raceEthnicCode', '');
      this.extractFirstCode('race2Code', '');
      this.extractFirstCode('race3Code', '');
      this.extractFirstCode('race4Code', '');
      this.extractFirstCode('race5Code', '');
      this.extractFirstCode('ethnicSubGroup');
      this.extractFirstCode('stateCode');
      this.extractFirstCode('highestDegree');
      this.extractFirstCode('subjectMatterArea');
      this.extractFirstCode('certificateType');
      this.extractFirstCode('residencyStatus');
    },
    editPerson() {
      this.cacheValues();
      this.editCurrentPanel = true;
      this.hideNotification();
    },
    extractFirstCode(field, fallback = null) {
      const value = this.initialInfo[field];
      if (Array.isArray(value)) {
        this.initialInfo[field] = value.length ? value[0].code : fallback;
      }
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.person.personType);
    },
    fieldLength(code) {
      return this.getFieldLengthsByPersonType(code, 'ASSIGN', this.person.personType);
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.person.personType);
    },
    fieldRequired(code) {
      return this.getFieldRequiredForPersonType(code, 'ASSIGN', this.person.personType);
    },
    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : null;
    },
    getCdfValidValues(cdf) {
      if (this.activeCdfValidValues) {
        return this.activeCdfValidValues.filter(v => v.fieldCode === cdf);
      }
      return [];
    },
    getCdfDisplayValue(cdf) {
      const studentCdf = this.initialInfo && this.initialInfo.studentCdf;
      const value = studentCdf ? studentCdf[cdf] : null;
      if (Array.isArray(value)) {
        return value.length ? (value[0].fieldValue || value[0].code || '') : '';
      }
      if (value && typeof value === 'object') {
        return value.fieldValue || value.code || '';
      }
      return value || '';
    },
    initialData() {
      return {
        cachedInfo: {},
        editCurrentPanel: false,
        userPersonTypes: this.$store.state.user.userPersonTypes
          .map(item => item.personType).filter(a => a.status === true),

        hasErrorFirstName: false,
        hasErrorMiddleName: false,
        hasErrorLastName: false,
        hasErrorSuffix: false,
        hasErrorPreviousLastName: false,
        hasErrorAkaName: false,
        hasErrorFullName: false,
        hasErrorDateOfBirth: false,
        hasErrorSSN: false,
        hasErrorEthnicityIndicator: false,
        hasErrorRaceEthnicCode: false,
        hasErrorRace2Code: false,
        hasErrorRace3Code: false,
        hasErrorRace4Code: false,
        hasErrorRace5Code: false,
        hasErrorEthnicSubGroup: false,

        hasErrorStreet1: false,
        hasErrorStreet2: false,
        hasErrorCity: false,
        hasErrorState: false,
        hasErrorZip: false,
        hasErrorGeocode: false,

        hasErrorHighestDegree: false,
        hasErrorSubjectMatterArea: false,
        hasErrorDegreeProvider: false,
        hasErrorCertificateNumber: false,
        hasErrorCertificateType: false,

        hasErrorBirthLocation: false,
        hasErrorResidencyStatus: false,
        hasErrorResidencyExpirationDate: false,
        hasErrorMothersMaidenName: false,

        hasErrorCdf1: false,
        hasErrorCdf2: false,
        hasErrorCdf3: false,
        hasErrorCdf4: false,
        hasErrorCdf5: false,
        hasErrorCdf6: false,
        hasErrorCdf7: false,
        hasErrorCdf8: false,
        hasErrorCdf9: false,
        hasErrorCdf10: false,
        hasErrorCdf11: false,

        initialInfo: {},

        msgFirstName: null,
        msgMiddleName: null,
        msgLastName: null,
        msgSuffix: null,
        msgPreviousLastName: null,
        msgAkaName: null,
        msgFullName: null,
        msgDateOfBirth: null,
        msgSSN: null,
        msgEthnicityIndicator: null,
        msgRaceEthnicCode: null,
        msgRace2Code: null,
        msgRace3Code: null,
        msgRace4Code: null,
        msgRace5Code: null,
        msgEthnicSubGroup: null,

        msgStreet1: null,
        msgStreet2: null,
        msgCity: null,
        msgState: null,
        msgZip: null,
        msgGeocode: null,

        msgHighestDegree: null,
        msgSubjectMatterArea: null,
        msgDegreeProvider: null,
        msgCertificateNumber: null,
        msgCertificateType: null,

        msgBirthLocation: null,
        msgResidencyStatus: null,
        msgResidencyExpirationDate: null,
        msgMothersMaidenName: null,

        msgCdf1: null,
        msgCdf2: null,
        msgCdf3: null,
        msgCdf4: null,
        msgCdf5: null,
        msgCdf6: null,
        msgCdf7: null,
        msgCdf8: null,
        msgCdf9: null,
        msgCdf10: null,
        msgCdf11: null,

        formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
        loading: false,
        loadingError: false,
      };
    },
    isLookup(cdf) {
      const l = this.getCdfValidValues(cdf);
      return l && l.length > 0;
    },
    isWholeNumbers(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 83) {
        evt.preventDefault();
      }
    },
    isSSN(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits, s, S and dash
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 83
          && charCode !== 45 && charCode !== 115) {
        evt.preventDefault();
      }
    },
    mapDescription(list, code) {
      const item = list.find(e => e.code === code);
      return item ? item.description : '\u00A0';
    },

    mapCertificateType() {
      return this.mapDescription(this.activeCertificateTypes, this.initialInfo.certificateType);
    },

    mapEthnicity() {
      return this.mapDescription(this.activeEthnicities, this.initialInfo.ethnicityIndicator);
    },

    mapEthnicSubGroup(ethnicSubGroup) {
      return this.mapDescription(this.activeEthnicSubGroups, ethnicSubGroup);
    },

    mapGender() {
      return this.mapDescription(this.activeGenders, this.initialInfo.genderCode);
    },

    mapHighestDegree() {
      return this.mapDescription(this.activeHighestDegrees, this.initialInfo.highestDegree);
    },

    mapRace(race) {
      return this.mapDescription(this.activeRaces, race);
    },

    mapResidencyStatus() {
      return this.mapDescription(this.activeResidencyStatuses, this.initialInfo.residencyStatus);
    },

    mapState() {
      return this.mapDescription(this.activeStates, this.initialInfo.stateCode);
    },

    mapSubjectMatterArea() {
      return this.mapDescription(this.activeSubjectMatterAreas, this.initialInfo.subjectMatterArea);
    },
    modifyRaces() {
      const raceCodes = [
        'raceEthnicCode',
        'race2Code',
        'race3Code',
        'race4Code',
        'race5Code',
      ];

      raceCodes.forEach((raceCode) => {
        if (!this.initialInfo[raceCode] || this.initialInfo[raceCode] === '' || this.initialInfo[raceCode] === null) {
          this.initialInfo[raceCode] = [];
        } else {
          this.initialInfo[raceCode] = this.activeRaces
            .filter(a => a.code === this.initialInfo[raceCode]);
        }
      });
    },
    nullifyDisabledFields() {
      const fields = [
        ['FIRST_NAME', 'firstName'],
        ['MIDDLE_NAME', 'middleName'],
        ['LAST_NAME', 'lastName'],
        ['SUFFIX', 'suffix'],
        ['PREVIOUS_LAST_NAME', 'previousLastName'],
        ['FULL_NAME', 'fullName'],
        ['AKA_NAME', 'akaName'],
        ['GENDER_CODE', 'genderCode'],
        ['DATE_OF_BIRTH', 'dateOfBirth'],
        ['SOCIAL_SECURITY_NUMBER', 'socialSecurityNumber'],
        ['ETHNICITY_INDICATOR', 'ethnicityIndicator'],
        ['RACE_ETHNIC_CODE', 'raceEthnicCode'],
        ['RACE2_CODE', 'race2Code'],
        ['RACE3_CODE', 'race3Code'],
        ['RACE4_CODE', 'race4Code'],
        ['RACE5_CODE', 'race5Code'],
        ['ETHNIC_SUB_GROUP', 'ethnicSubGroup'],
        ['STATE_STUDENT_ID', 'stateStudentId'],

        ['LOCAL_STUDENT_ID', 'localId'],
        ['GRADE_CODE', 'gradeCode'],

        ['STREET_1', 'street1'],
        ['STREET_2', 'street2'],
        ['CITY', 'city'],
        ['STATE_CODE', 'stateCode'],
        ['ZIP', 'zip'],
        ['GEOCODE', 'geocode'],

        ['HIGHEST_DEGREE', 'highestDegree'],
        ['SUBJECT_MATTER_AREA', 'subjectMatterArea'],
        ['DEGREE_PROVIDER', 'degreeProvider'],
        ['CERTIFICATE_NUMBER', 'certificateNumber'],
        ['CERTIFICATE_TYPE', 'certificateType'],

        ['BIRTH_LOCATION', 'birthLocation'],
        ['RESIDENCY_STATUS', 'residencyStatus'],
        ['RESIDENCY_EXPIRATION_DATE', 'residencyExpirationDate'],
        ['MOTHERS_MAIDEN_NAME', 'mothersMaidenName'],

        ['ACTIVE', 'active'],
        ['SOURCE_REFRESH_DATE', 'sourceRefreshDate'],

        ['cdf_1', 'cdf_1'],
        ['cdf_2', 'cdf_2'],
        ['cdf_3', 'cdf_3'],
        ['cdf_4', 'cdf_4'],
        ['cdf_5', 'cdf_5'],
        ['cdf_6', 'cdf_6'],
        ['cdf_7', 'cdf_7'],
        ['cdf_8', 'cdf_8'],
        ['cdf_9', 'cdf_9'],
        ['cdf_10', 'cdf_10'],
        ['cdf_11', 'cdf_11'],

        ['ITINERANT_FLAG', 'itinerantFlag'],
        ['LOCATION_COMMENT', 'comments'],
        ['RES_DISTRICT_CODE', 'resDistrictCode'],
        ['ALT_STUDENT_ID', 'altStudentId'],
        ['ALT_SOURCE_SIS_CODE', 'altSourceSisCode'],
      ];
      fields.forEach((e) => {
        if (!this.fieldEnabled(e[0])) {
          if (e[0].includes('cdf_')) {
            this.initialInfo.studentCdf[e[1]] = null;
          } else if (e[0].includes('RACE')) {
            this.initialInfo[e[1]] = '';
          } else {
            this.initialInfo[e[1]] = null;
          }
        }
      });
    },
    onPasteValidation(fieldName, event) {
      if (event && event.clipboardData) {
        // Filtering logic (only digits)
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text');
        const digitsOnly = pastedData.replace(/\D+/g, '');
        this[fieldName] = digitsOnly;
      }

      // Run validation after paste completes
      this.$nextTick(() => {
        this.validateInputField(fieldName);
      });
    },
    resetData() {
      this.initialInfo = this.cachedInfo;
    },
    resetErrors() {
      const fields = [
        'AkaName', 'BirthLocation', 'CertificateNumber', 'Cdf1', 'Cdf10', 'Cdf11', 'Cdf2', 'Cdf3', 'Cdf4', 'Cdf5', 'Cdf6', 'Cdf7', 'Cdf8', 'Cdf9',
        'City', 'DegreeProvider', 'DateOfBirth', 'FirstName', 'FullName', 'LastName', 'MiddleName', 'MothersMaidenName', 'PreviousLastName', 'SSN',
        'Street1', 'Street2', 'Suffix', 'Zip', 'Geocode',
      ];

      fields.forEach((field) => {
        this[`hasError${field}`] = false;
        this[`msg${field}`] = null;
      });
    },
    resetNotifications() {
      this.hideNotification();
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    isMaskingEnabled(value) {
      return !!value && /[^0-9-]/.test(value);
    },
    updateRaces(race, value) {
      const raceCodes = {
        raceEthnicCode: ['resetRace2Code', 'resetRace3Code', 'resetRace4Code', 'resetRace5Code'],
        race2Code: ['resetRace3Code', 'resetRace4Code', 'resetRace5Code'],
        race3Code: ['resetRace4Code', 'resetRace5Code'],
        race4Code: ['resetRace5Code'],
      };

      const clearReferences = (references) => {
        references.forEach((ref) => {
          if (this.$refs[ref]) {
            this.$refs[ref].clear();
          }
        });
      };

      if (raceCodes[race]) {
        if (!value.length) {
          this.initialInfo[race] = [];
          clearReferences(raceCodes[race]);
        } else {
          this.$set(this.initialInfo, race, [value[0]]);
        }
      }
    },
    updateLookupCdfValue(cdf, value) {
      this.initialInfo.studentCdf[cdf] = value.length ? value[0].fieldValue : null;
      this.validateInputField(cdf);
    },
    async updatePerson() {
      if (this.checkFieldValidations()) return;
      if (this.noDataChange) {
        this.hideNotification();
        this.editCurrentPanel = false;
        return;
      }
      if (this.currentPersonTypeCategory === 'EMPLOYEE') {
        delete this.initialInfo.gradeCode;
      }
      this.initialInfo.residencyExpirationDate = this
        .formatDate(this.initialInfo.residencyExpirationDate);
      // sourceRefreshDate is not editable in this panel, so its value (which may
      // carry a time component from migration/API) is passed through untouched to
      // retain the existing time. Editing the date is only done from the location
      // panel, where the time is zeroed on change.
      if (this.initialInfo.socialSecurityNumber) {
        this.initialInfo.socialSecurityNumber = this.initialInfo.socialSecurityNumber.replace(/-/g, '');
      }
      if (!this.hasModule('PRSID:PRPRF:SSN:EDIT') || this.initialInfo.socialSecurityNumber === '') {
        this.initialInfo.socialSecurityNumber = null;
      }
      this.initialInfo.studentCdf.cdf_11 = this.getCdfDisplayValue('cdf_11') || null;
      this.cleanUpFields();
      this.hideNotification();
      this.loading = true;
      this.nullifyDisabledFields();
      try {
        await axios.put(`/person/${this.person.stateStudentId}`, this.initialInfo);
        this.showNotification('success', ['The person information has been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (error) {
        if (error.response) {
          const errorMessages = error.response.data.message
            .split('\n')
            .map(msg => msg.trim())
            .filter(msg => msg); // removes empty lines
          this.showNotification('danger', errorMessages);
        }
      }
      this.$root.$emit('reloadPerson');
    },
    validateInputField(field) {
      const makeField = (code, hasErrorKey, msgKey) => [
        code, hasErrorKey, msgKey, this.fieldLength(code),
      ];

      const Obj = {
        firstName: makeField('FIRST_NAME', 'hasErrorFirstName', 'msgFirstName'),
        middleName: makeField('MIDDLE_NAME', 'hasErrorMiddleName', 'msgMiddleName'),
        lastName: makeField('LAST_NAME', 'hasErrorLastName', 'msgLastName'),
        suffix: makeField('SUFFIX', 'hasErrorSuffix', 'msgSuffix'),
        previousLastName: makeField('PREVIOUS_LAST_NAME', 'hasErrorPreviousLastName', 'msgPreviousLastName'),
        akaName: makeField('AKA_NAME', 'hasErrorAkaName', 'msgAkaName'),
        fullName: makeField('FULL_NAME', 'hasErrorFullName', 'msgFullName'),

        street1: makeField('STREET_1', 'hasErrorStreet1', 'msgStreet1'),
        street2: makeField('STREET_2', 'hasErrorStreet2', 'msgStreet2'),
        city: makeField('CITY', 'hasErrorCity', 'msgCity'),
        zip: makeField('ZIP', 'hasErrorZip', 'msgZip'),
        geocode: makeField('GEOCODE', 'hasErrorGeocode', 'msgGeocode'),

        degreeProvider: makeField('DEGREE_PROVIDER', 'hasErrorDegreeProvider', 'msgDegreeProvider'),
        certificateNumber: makeField('CERTIFICATE_NUMBER', 'hasErrorCertificateNumber', 'msgCertificateNumber'),

        birthLocation: makeField('BIRTH_LOCATION', 'hasErrorBirthLocation', 'msgBirthLocation'),
        mothersMaidenName: makeField('MOTHERS_MAIDEN_NAME', 'hasErrorMothersMaidenName', 'msgMothersMaidenName'),

        cdf_1: makeField('cdf_1', 'hasErrorCdf1', 'msgCdf1'),
        cdf_2: makeField('cdf_2', 'hasErrorCdf2', 'msgCdf2'),
        cdf_3: makeField('cdf_3', 'hasErrorCdf3', 'msgCdf3'),
        cdf_4: makeField('cdf_4', 'hasErrorCdf4', 'msgCdf4'),
        cdf_5: makeField('cdf_5', 'hasErrorCdf5', 'msgCdf5'),
        cdf_6: makeField('cdf_6', 'hasErrorCdf6', 'msgCdf6'),
        cdf_7: makeField('cdf_7', 'hasErrorCdf7', 'msgCdf7'),
        cdf_8: makeField('cdf_8', 'hasErrorCdf8', 'msgCdf8'),
        cdf_10: makeField('cdf_10', 'hasErrorCdf10', 'msgCdf10'),
        cdf_11: makeField('cdf_11', 'hasErrorCdf11', 'msgCdf11'),
      };

      if (Object.keys(Obj).includes(field)) {
        const [code, hasError, msg, maxLength] = Obj[field];
        if (code.includes('cdf_')) {
          if (this.fieldRequired(code) && !this.initialInfo.studentCdf[field]) {
            this[hasError] = true;
            this[msg] = `${this.fieldLabel(code)} is required.`;
          } else if (this.initialInfo.studentCdf[field]
              && this.initialInfo.studentCdf[field].length > maxLength) {
            this[hasError] = true;
            this[msg] = `${this.fieldLabel(code)
            } allows a maximum of ${maxLength} characters.`;
          } else {
            this[hasError] = false;
            this[msg] = null;
          }
        } else if (this.fieldRequired(code) && !this.initialInfo[field]) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is required.`;
        } else if (this.initialInfo[field] && this.initialInfo[field].length > maxLength) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)
          } allows a maximum of ${maxLength} characters.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
    validateField(field) {
      if (field === 'dateOfBirth') {
        if (this.initialInfo.dateOfBirth === '' || !this.initialInfo.dateOfBirth) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.getFieldLabel(`PER:${this.person.personType}:ASSIGN:DATE_OF_BIRTH`)
          } is required.`;
        } else if (this.initialInfo.dateOfBirth && !this.isValidDateFormat(this.initialInfo.dateOfBirth, 'YYYY-MM-DD')) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.getFieldLabel(`PER:${this.person.personType}:ASSIGN:DATE_OF_BIRTH`)
          } is invalid.`;
        } else if (this.initialInfo.dateOfBirth && !this.futureBirthYearsAllowed
          && !this.isValidDOB(this.initialInfo.dateOfBirth, 'YYYY-MM-DD')) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.getFieldLabel(`PER:${this.person.personType}:ASSIGN:DATE_OF_BIRTH`)
          } is invalid.`;
        } else if (this.initialInfo.dateOfBirth && this.futureBirthYearsAllowed
          && !this.isValidDOBWithfutureBirthYearsAllowed(this.initialInfo.dateOfBirth, 'YYYY-MM-DD')) {
          this.hasErrorDateOfBirth = true;
          this.msgDateOfBirth = `${this.getFieldLabel(`PER:${this.person.personType}:ASSIGN:DATE_OF_BIRTH`)
          } is invalid.`;
        } else {
          this.hasErrorDateOfBirth = false;
          this.msgDateOfBirth = null;
        }
      }
      if (field === 'ssn') {
        let ssnPattern;
        if (this.allowSinSsn) {
          // Allows:
          // - 9 digits: 123456789
          // - S followed by 8 digits: S12345678 or s12345678
          // - Dashed SSNs: 123-45-6789
          // - S followed by dashed: S12-34-5678 or s12-34-5678
          ssnPattern = /^(?:\d{9}|[Ss]\d{8}|\d{3}-\d{2}-\d{4}|[Ss]\d{2}-\d{2}-\d{4})$/;
        } else {
          // Only allows:
          // - 9 digits: 123456789
          // - Dashed SSNs: 123-45-6789
          // NO S/s prefix
          ssnPattern = /^(?:\d{9}|\d{3}-\d{2}-\d{4})$/;
        }

        const rawSSN = this.initialInfo.socialSecurityNumber;
        const ssn = rawSSN && rawSSN.trim() !== '' ? rawSSN.trim() : null;

        const isRequired = this.fieldRequired('SOCIAL_SECURITY_NUMBER');

        if (isRequired && !ssn) {
          this.hasErrorSSN = true;
          this.msgSSN = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} is required.`;
        } else if (ssn && this.hasModule('PRSID:PRPRF:SSN:EDIT') && !ssnPattern.test(ssn)) {
          this.hasErrorSSN = true;
          this.msgSSN = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} must be a valid format.`;
        } else {
          this.hasErrorSSN = false;
          this.msgSSN = null;
        }
      }
    },
    validateDateField(field) {
      const Obj = {
        residencyExpirationDate: ['RESIDENCY_EXPIRATION_DATE', 'hasErrorResidencyExpirationDate', 'msgResidencyExpirationDate'],
        cdf_9: ['cdf_9', 'hasErrorCdf9', 'msgCdf9'],
      };
      if (Object.keys(Obj).includes(field)) {
        const [code, hasError, msg] = Obj[field];
        if (code.includes('cdf_')) {
          if (this.fieldRequired(code) && !this.initialInfo.studentCdf[field]) {
            this[hasError] = true;
            this[msg] = `${this.fieldLabel(code)} is required.`;
          } else if (this.fieldRequired(code)
              && this.initialInfo.studentCdf[field]
              && !this.isValidDateFormat(this.initialInfo.studentCdf[field], 'YYYY-MM-DD')) {
            this[hasError] = true;
            this[msg] = `${this.fieldLabel(code)} is invalid.`;
          } else {
            this[hasError] = false;
            this[msg] = null;
          }
        } else if (this.fieldRequired(code) && !this.initialInfo[field]) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is required.`;
        } else if (this.initialInfo[field] && !this.isValidDateFormat(this.initialInfo[field], 'YYYY-MM-DD')) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(code)} is invalid.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
    validateSelectField(field) {
      const ObjEsSelect = {
        ethnicityIndicator: ['ETHNICITY_INDICATOR', 'hasErrorEthnicityIndicator', 'msgEthnicityIndicator'],
        raceEthnicCode: ['RACE_ETHNIC_CODE', 'hasErrorRaceEthnicCode', 'msgRaceEthnicCode'],
        race2Code: ['RACE2_CODE', 'hasErrorRace2Code', 'msgRace2Code'],
        race3Code: ['RACE3_CODE', 'hasErrorRace3Code', 'msgRace3Code'],
        race4Code: ['RACE4_CODE', 'hasErrorRace4Code', 'msgRace4Code'],
        race5Code: ['RACE5_CODE', 'hasErrorRace5Code', 'msgRace5Code'],
        ethnicSubGroup: ['ETHNIC_SUB_GROUP', 'hasErrorEthnicSubGroup', 'msgEthnicSubGroup'],

        stateCode: ['STATE_CODE', 'hasErrorState', 'msgState'],

        highestDegree: ['HIGHEST_DEGREE', 'hasErrorHighestDegree', 'msgHighestDegree'],
        subjectMatterArea: ['SUBJECT_MATTER_AREA', 'hasErrorSubjectMatterArea', 'msgSubjectMatterArea'],
        certificateType: ['CERTIFICATE_TYPE', 'hasErrorCertificateType', 'msgCertificateType'],

        residencyStatus: ['RESIDENCY_STATUS', 'hasErrorResidencyStatus', 'msgResidencyStatus'],
      };
      if (Object.keys(ObjEsSelect).includes(field)) {
        const [code, hasError, msg] = ObjEsSelect[field];
        if (this.fieldRequired(code)
          && (!this.initialInfo[field] || this.initialInfo[field].length === 0)) {
          this[hasError] = true;
          this[msg] = `${this.fieldLabel(`${code}`)} is required.`;
        } else {
          this[hasError] = false;
          this[msg] = null;
        }
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .action-buttons
    border-top: 1px solid #dee2e6
    margin: 0rem 2rem !important
    padding: 1rem 2rem 1rem 0rem !important
  .border-left
    border-left: 1px solid #dee2e6
  .control
    padding-left: 30px
  .columns
    padding-left: 2rem
    padding-top: 2rem
    display: flex
    flex-wrap: wrap
  .column
    padding: 0
    flex: 1 0 auto
  .header
    display: flex
    align-items: center
  .icon-right
    padding-right: 1rem
  .input
    padding-top: 0 !important
  .label
    width: 140px
  .no-pointer
    pointer-events: none
    &:hover
      text-decoration:  none
  .padding--1rem
    padding: 1rem
  .reason-header
    color: #555555
    font-size: 13.6px
    font-weight: 700
    text-align: left
    padding-left: 1rem
  .reason-text
    color: #212529
    font-size: 13.6px
    font-weight: 400
    text-align: left
    padding-left: 1rem
  .retired
    color: #DC3545 !important
    font-size: 12px !important
  .retiredId
    color: #737373
    font-size: 13.6px
    font-weight: 400
  .content
    padding: 1rem 0
</style>
